// Helpers shared by the Content Analysis redesign widgets (Overview, Topics &
// Subtopics, Comments Analysis) on the list and the detail page.

export const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

// Brand colours, as the Overview design draws the posts bar and the comments
// donut. Anything else (a platform added later) takes a neutral fallback.
const PLATFORM_COLORS: Record<string, string> = {
  facebook: '#1D7CF2',
  instagram: '#C32F86',
  linkedin: '#0E62B8',
  tiktok: '#111111',
  twitter: '#5A6FC0',
  youtube: '#E8231F',
}
const FALLBACK_COLORS = ['#8080FF', '#9AA1AB', '#FFB38A', '#C7CED1']

export const platformColor = (platform: string, index = 0): string =>
  PLATFORM_COLORS[platform?.toLowerCase()] ??
  FALLBACK_COLORS[index % FALLBACK_COLORS.length]

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  twitter: 'X (Twitter)',
  youtube: 'YouTube',
}

export const platformLabel = (platform?: string | null): string => {
  const key = String(platform ?? '').toLowerCase()
  if (!key) return ''
  return PLATFORM_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
}

export const formatCount = (value?: number | null): string =>
  (value ?? 0).toLocaleString('en-US')

/** "Maia Kapanadze" → "Maia K."; a single name stays as it is. */
export const shortName = (author?: string | null): string => {
  const parts = String(author ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return 'Unknown'
  if (parts.length === 1) return parts[0]
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`
}

export const initialOf = (author?: string | null): string => {
  const first = String(author ?? '')
    .trim()
    .charAt(0)
  if (!first) return '?'
  const upper = first.toUpperCase()
  // Georgian has no everyday capitals: toUpperCase turns "ნ" into Mtavruli
  // "Ნ", which reads oddly and most fonts lack. Keep the letter as written.
  return /[\u1C90-\u1CBF]/.test(upper) ? first : upper
}

const plural = (count: number, unit: string) =>
  `${count} ${unit}${count === 1 ? '' : 's'} ago`

/**
 * "3 days ago" from an absolute timestamp. Computed client-side so the label
 * stays right while the page is left open; empty when there is no time.
 */
export const timeAgo = (iso?: string | null): string => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return plural(minutes, 'minute')
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return plural(hours, 'hour')
  const days = Math.floor(hours / 24)
  if (days < 30) return plural(days, 'day')
  const months = Math.floor(days / 30)
  if (months < 12) return plural(months, 'month')
  return plural(Math.floor(months / 12), 'year')
}

/** "Oct 6" — the AI insight's "Week of …" label. */
export const shortDate = (iso?: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Tbilisi',
  })
}

/** "May 4, 14:21" — when the data was last updated. */
export const shortDateTime = (iso?: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime())) return ''
  // Tbilisi time, the one dating rule the backend uses, whatever the
  // viewer's own zone.
  const day = date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Tbilisi',
  })
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tbilisi',
  })
  return `${day}, ${time}`
}

export const percent = (value: number, total: number): number =>
  total > 0 ? Math.round((value / total) * 100) : 0

export type ScoreBand = 'Neutral' | 'Poor' | 'Positive'

// The app-wide 60 / 40 bands (Social Listening's SentimentScoreCard).
export const scoreBand = (score: number): ScoreBand => {
  if (score >= 60) return 'Positive'
  if (score >= 40) return 'Neutral'
  return 'Poor'
}

// Placeholder copy per band until the designer's sentences land.
export const SCORE_SENTENCE: Record<ScoreBand, string> = {
  Neutral:
    'The conversation around your content is mixed, with no clear lean either way',
  Poor: 'Negative comments outweigh positive ones in the conversation around your content',
  Positive: 'Most of the conversation around your content is favorable',
}
